import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useReship } from "@/context/ReshipContext";
import { useStatuses } from "@/context/StatusContext";
import ReshipDialog from "@/components/ReshipDialog";
import SavedViews from "@/components/SavedViews";
import StatusBadge, { StageBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PackagePlus, RefreshCw, ArrowRight, Loader2, Truck, Search } from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric", timeZone: "Asia/Kolkata" }); } catch { return "—"; }
}

export default function NewShipments() {
  const [tab, setTab] = useState("pending");
  const [pending, setPending] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reshipFor, setReshipFor] = useState(null);
  const { refresh: refreshCount } = useReship();
  const { stages } = useStatuses();
  const [hidden, setHidden] = useState(() => new Set());
  const [shippedSearch, setShippedSearch] = useState("");
  const [pendingSearch, setPendingSearch] = useState("");
  const pendingRows = useMemo(() => {
    const q = pendingSearch.trim().toLowerCase();
    if (!q) return pending;
    return pending.filter((p) => [p.order_id, p.tracking_number, p.courier_partner, p.city, p.state]
      .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [pending, pendingSearch]);
  const navigate = useNavigate();

  const toggleStatus = (key) => setHidden((prev) => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const visibleGroups = useMemo(() => {
    const q = shippedSearch.trim().toLowerCase();
    return groups
      .map((g) => ({ ...g, shipments: g.shipments.filter((s) => !hidden.has(s.stage)) }))
      .filter((g) => g.shipments.length > 0)
      .filter((g) => !q || (g.root_order_id || "").toLowerCase().includes(q)
        || g.shipments.some((s) => (s.order_id || "").toLowerCase().includes(q) || (s.tracking_number || "").toLowerCase().includes(q)));
  }, [groups, hidden, shippedSearch]);

  const load = useCallback((t) => {
    setLoading(true);
    if (t === "pending") {
      api.get("/pending-reshipments")
        .then((r) => setPending(Array.isArray(r.data) ? r.data : []))
        .catch(() => setPending([]))
        .finally(() => setLoading(false));
    } else {
      api.get("/shipments/multi-shipments")
        .then((r) => setGroups(Array.isArray(r.data) ? r.data : []))
        .catch(() => setGroups([]))
        .finally(() => setLoading(false));
    }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);

  return (
    <div className="p-8 max-w-5xl mx-auto space-y-6" data-testid="new-shipments-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl bg-[#0B3D3D] text-[#C5A253] flex items-center justify-center"><PackagePlus className="h-5 w-5" /></span>
          <div>
            <h1 className="font-heading text-2xl font-extrabold text-[#111411] tracking-tight">New Shipments</h1>
            <p className="text-sm text-muted-foreground">Replacements awaiting details, and every order that's had a new shipment sent</p>
          </div>
        </div>
        <SavedViews page="new-shipments"
          filters={{ tab, hidden: Array.from(hidden) }}
          fields={[
            { key: "tab", label: "Open tab", type: "select", options: [{ value: "pending", label: "Pending Data" }, { value: "shipped", label: "Shipped" }] },
            { key: "hidden", label: "Hide statuses", type: "multi", options: stages.map((s) => ({ value: s.key, label: s.label })), hint: "Struck-through statuses will be hidden in the Shipped list." },
          ]}
          onApply={(f) => { if (f.tab) setTab(f.tab); setHidden(new Set(f.hidden || [])); }} />
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        <button data-testid="ns-tab-pending" onClick={() => setTab("pending")}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "pending" ? "border-[#0B3D3D] text-[#0B3D3D]" : "border-transparent text-muted-foreground hover:text-[#111411]"}`}>
          Pending Data {pending.length > 0 && tab === "pending" && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#C24127] text-white text-[10px] font-bold align-middle">{pending.length}</span>}
        </button>
        <button data-testid="ns-tab-shipped" onClick={() => setTab("shipped")}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === "shipped" ? "border-[#0B3D3D] text-[#0B3D3D]" : "border-transparent text-muted-foreground hover:text-[#111411]"}`}>
          Shipped
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…</div>
      ) : tab === "pending" ? (
        <div className="rounded-lg border border-border bg-white overflow-hidden">
          {pending.length === 0 ? (
            <div className="p-16 text-center" data-testid="new-shipments-empty">
              <PackagePlus className="h-10 w-10 mx-auto text-[#C5A253] mb-3" />
              <p className="font-heading font-bold text-[#111411]">All caught up</p>
              <p className="text-sm text-muted-foreground mt-1">No new shipments are awaiting details right now.</p>
            </div>
          ) : (
            <>
              <div className="p-3 border-b border-border">
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input data-testid="ns-pending-search" value={pendingSearch} onChange={(e) => setPendingSearch(e.target.value)}
                         placeholder="Search order ID, tracking or courier…" className="pl-9 h-9" />
                </div>
              </div>
              <div className="divide-y divide-border">
                {pendingRows.length === 0 ? (
                  <div className="p-10 text-center text-sm text-muted-foreground" data-testid="ns-pending-filtered-empty">No matches for “{pendingSearch}”.</div>
                ) : pendingRows.map((p) => (
                <div key={p.id} data-testid={`pending-reship-${p.id}`}
                     className="flex flex-wrap items-center gap-3 p-4 hover:bg-[#FAFAF7] transition-colors">
                  <div className="flex items-center gap-2 text-[#B45309]"><RefreshCw className="h-4 w-4" /></div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link to={`/shipments/${p.id}`} className="font-semibold text-[#111411] hover:text-[#0B3D3D]">#{p.order_id}</Link>
                      <StatusBadge status={p.status} />
                    </div>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      AWB {p.tracking_number} · {p.courier_partner} · {p.city || "—"}{p.state ? `, ${p.state}` : ""} · Deadline {fmtDate(p.resolution_date)}
                    </p>
                  </div>
                  <Button size="sm" data-testid={`pending-fill-${p.id}`} onClick={() => setReshipFor(p)}
                          className="ml-auto gap-1.5 bg-[#0B3D3D] hover:bg-[#0A2E2E] text-white">
                    Add details <ArrowRight className="h-4 w-4" />
                  </Button>
                </div>
              ))}
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4" data-testid="shipped-list">
          {groups.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative min-w-[220px] flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input data-testid="shipped-search" value={shippedSearch} onChange={(e) => setShippedSearch(e.target.value)}
                       placeholder="Search order ID or tracking…" className="pl-9 h-9" />
              </div>
              <div className="flex flex-wrap items-center gap-1.5" data-testid="shipped-status-filter">
              <span className="text-xs text-muted-foreground mr-1">Show statuses:</span>
              {stages.map((st) => {
                const active = !hidden.has(st.key);
                return (
                  <button key={st.key} type="button" data-testid={`shipped-filter-${st.key}`}
                          onClick={() => toggleStatus(st.key)}
                          className={`text-[11px] font-medium rounded-full px-2.5 py-1 border transition-colors ${
                            active ? "bg-[#0B3D3D] text-white border-[#0B3D3D]" : "bg-white text-muted-foreground border-border line-through"
                          }`}>
                    {st.label}
                  </button>
                );
              })}
              </div>
            </div>
          )}
          {groups.length === 0 ? (
            <div className="rounded-lg border border-border bg-white p-16 text-center" data-testid="shipped-empty">
              <Truck className="h-10 w-10 mx-auto text-[#C5A253] mb-3" />
              <p className="font-heading font-bold text-[#111411]">No new shipments yet</p>
              <p className="text-sm text-muted-foreground mt-1">Orders that get a replacement shipment will be grouped here.</p>
            </div>
          ) : visibleGroups.length === 0 ? (
            <div className="rounded-lg border border-border bg-white p-12 text-center text-sm text-muted-foreground" data-testid="shipped-filtered-empty">
              No shipments match the selected statuses.
            </div>
          ) : (
            visibleGroups.map((g) => (
              <div key={g.root_id} data-testid={`shipped-group-${g.root_id}`}
                   className="rounded-lg border border-border bg-white overflow-hidden">
                <div className="flex items-center justify-between gap-2 px-4 py-2.5 bg-[#F4F6F4] border-b border-border">
                  <span className="text-sm font-semibold text-[#111411]">Order #{g.root_order_id}</span>
                  <span className="text-xs font-medium text-[#8A6D1F] bg-[#FBEFD9] rounded-full px-2 py-0.5">{g.count} shipments</span>
                </div>
                <div className="divide-y divide-border">
                  {g.shipments.map((s) => (
                    <button key={s.id} type="button" data-testid={`shipped-item-${s.id}`}
                            onClick={() => navigate(`/shipments/${s.id}`)}
                            className="w-full text-left flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-[#FAFAF7] transition-colors">
                      <span className="text-xs font-semibold uppercase tracking-wide text-[#8A6D1F] w-28 shrink-0">{s.seq_label}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-[#111411]">#{s.order_id}</span>
                          <StageBadge stage={s.stage} />
                          <StatusBadge status={s.status} />
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          AWB {s.tracking_number} · {s.courier_partner}
                          {s.is_overdue && <span className="text-[#C24127] font-medium"> · Overdue</span>}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <ReshipDialog shipment={reshipFor} onClose={() => setReshipFor(null)}
                    onDone={() => { load("pending"); refreshCount(); }} />
    </div>
  );
}
